import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';

// Premium Logistics Hubs Index - Architectural Harmony with InvoiceShow
export default function LocationsIndex() {
    const location = useLocation();
    const [locations, setLocations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        fetchLocations();
    }, [location, currentPage, searchTerm]);

    const fetchLocations = async () => {
        try {
            setLoading(true);
            const params: any = { 
                page: currentPage,
                q: searchTerm
            };
            const response = await api.get('/locations', { params });
            if (response.success) {
                setLocations(response.data);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to deactivate this location?')) return;
        try {
            const response = await api.delete(`/locations/${id}`);
            if (response.success) {
                fetchLocations();
            }
        } catch (error) {
            console.error('Error deleting location:', error);
        }
    };

    if (loading && locations.length === 0) return <TableSkeleton />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Styled like InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Logistics <span className="text-indigo-600">Hubs</span>
                        </h1>
                        <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {pagination.total_count} Warehouses
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 ml-10">
                        Managing global <span className="font-semibold text-slate-700">inventory & distribution</span> nodes
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Link
                        to="/locations/new"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-black transition-all shadow-sm shadow-slate-200"
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
                        New Warehouse
                    </Link>
                </div>
            </div>

            {/* Metrics Row - Segmented Layout */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100 uppercase tracking-widest mb-8">
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Network Capacity</h3>
                    <p className="text-xl font-black text-slate-900 font-mono">{pagination.total_count}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Global Hubs Identified</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Operational Nodes</h3>
                    <p className="text-xl font-black text-emerald-600 font-mono">{locations.filter(l => l.active).length}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Active Status</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Primary Gateway</h3>
                    <p className="text-xl font-black text-amber-600 font-mono">{locations.filter(l => l.is_primary).length}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Core Distribution Hubs</p>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative group max-w-md">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search hubs by name or address..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-11 pr-4 py-3 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm font-medium transition-all shadow-sm"
                    />
                </div>
            </div>

            {/* Grid Layout - Premium Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {locations.map((loc) => (
                    <div
                        key={loc.id}
                        className="group bg-white border border-slate-200 rounded-2xl p-6 transition-all hover:shadow-lg hover:shadow-slate-200/50 hover:border-indigo-200 relative overflow-hidden"
                    >
                        {/* Status Badges */}
                        <div className="absolute top-4 right-4 flex gap-2">
                            {loc.is_primary && (
                                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-tighter rounded border border-amber-100">
                                    Primary Gateway
                                </span>
                            )}
                            <span className={`px-2 py-0.5 ${loc.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-50 text-slate-500 border border-slate-100'} text-[9px] font-black uppercase tracking-tighter rounded`}>
                                {loc.active ? 'Operational' : 'Deactivated'}
                            </span>
                        </div>

                        {/* Visual Asset Container */}
                        <div className="w-14 h-14 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-indigo-50 group-hover:border-indigo-100 transition-all duration-300">
                            <svg className="w-8 h-8 text-slate-400 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                            </svg>
                        </div>

                        {/* Identity Context */}
                        <h3 className="text-lg font-black text-slate-900 mb-1 group-hover:text-indigo-600 transition-colors truncate">
                            {loc.name}
                        </h3>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">NODE ID: HUB-{loc.id.toString().padStart(3, '0')}</div>
                        
                        <p className="text-sm text-slate-500 mb-6 line-clamp-2 min-h-[40px] font-medium leading-relaxed italic">
                            {loc.address || 'Deployment address pending configuration...'}
                        </p>

                        {/* Communication Protocol */}
                        <div className="space-y-2.5 pt-5 border-t border-slate-50">
                            <div className="flex items-center gap-3 text-[11px] text-slate-600 font-bold group-hover:translate-x-1 transition-transform">
                                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                {loc.phone || 'COMMS OFFLINE'}
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-slate-400 font-bold group-hover:translate-x-1 transition-transform delay-75">
                                <svg className="w-3.5 h-3.5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                                {loc.email || 'NO LIAISON RECORDED'}
                            </div>
                        </div>

                        {/* Operational Orchestration */}
                        <div className="mt-8 flex items-center justify-between pt-5">
                            <Link
                                to={`/locations/${loc.id}/edit`}
                                className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.15em] hover:text-indigo-800 transition-colors flex items-center gap-2 group/btn"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                Re-architect
                            </Link>
                            <button
                                onClick={() => handleDelete(loc.id)}
                                className="text-[10px] font-black text-slate-300 uppercase tracking-[0.15em] hover:text-rose-600 transition-colors hover:bg-rose-50 px-2.5 py-1 rounded"
                            >
                                Decommission
                            </button>
                        </div>
                    </div>
                ))}

                {locations.length === 0 && !loading && (
                    <div className="col-span-full py-20 text-center bg-white rounded-3xl border-2 border-dashed border-slate-100">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-200">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"/></svg>
                        </div>
                        <h3 className="text-xl font-black text-slate-900 mb-2">Network Infrastructure Empty</h3>
                        <p className="text-slate-500 mb-8 font-medium italic">No logistics hubs were identified matching your current parameters.</p>
                        <Link
                            to="/locations/new"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-black transition-all shadow-xl shadow-slate-200"
                        >
                            Initialize First Hub
                        </Link>
                    </div>
                )}
            </div>

            {/* Pagination Integration */}
            <div className="flex justify-end mt-4">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}
